import React from 'react';
import Footer from './Footer';

interface TermsPageProps {
  darkMode: boolean;
}

const TermsPage: React.FC<TermsPageProps> = ({ darkMode }) => {
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-100',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-[#858585]' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-700',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    link: darkMode ? 'text-[#4a9eff] hover:text-[#6bb5ff]' : 'text-blue-600 hover:text-blue-700',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} py-12 px-4`}>
      <div className={`max-w-4xl mx-auto ${theme.panelBg} rounded-lg shadow-lg p-8 ${theme.border} border`}>
        <h1 className={`text-3xl font-bold mb-6 ${theme.text}`}>Terms of Service</h1>
        <p className={`text-sm mb-8 ${theme.mutedText}`}>Last Updated: Dec 15, 2025</p>

        <div className="prose prose-invert max-w-none space-y-6">
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>1. Acceptance of Terms</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              By accessing and using the Proto Workbench tool available at{' '}
              <strong className="font-semibold">https://protobuf.echologai.com</strong>, you accept and agree to be bound by 
              these Terms of Service. If you do not agree to these terms, please do not use this tool.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              These terms apply specifically to the Proto Workbench tool. For comprehensive terms covering all EchoLog services, 
              please see our{' '}
              <a href="https://echologai.com/terms/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main Terms of Service
              </a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>2. Description of Service</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Proto Workbench is a free, browser-based tool that allows you to compile, decode, and visualize Protocol Buffers (Protobuf) data. 
              The tool processes data entirely in your browser using the protobuf.js library and does not send your data to external servers.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>3. Use of the Service</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>You agree to use Proto Workbench only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Use the tool for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to any part of the service</li>
              <li>Interfere with or disrupt the service or servers connected to the service</li>
              <li>Use automated systems (bots, scrapers) to access the service without permission</li>
              <li>Use the tool in any way that could damage, disable, or impair the service</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>4. No Warranty</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Proto Workbench is provided "as is" and "as available" without warranties of any kind, either express or 
              implied, including but not limited to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Accuracy, reliability, or completeness of results</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We do not guarantee that the tool will be error-free, uninterrupted, or free from viruses or other harmful components.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>5. Limitation of Liability</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              To the maximum extent permitted by law, EchoLog and its team shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or 
              indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Your use or inability to use Proto Workbench</li>
              <li>Any errors or omissions in the tool's functionality</li>
              <li>Any loss or damage of any kind incurred as a result of your use of the tool</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>6. Data and Privacy</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              While Proto Workbench processes data entirely in your browser and does not send your data to our servers, 
              you are responsible for:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Ensuring you have the right to process any data you input into the tool</li>
              <li>Complying with applicable data protection laws and regulations</li>
              <li>Not inputting sensitive data if you are concerned about browser-based processing</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more information about how we handle data, please see our{' '}
              <a href="/privacy" className={`${theme.link} underline`}>Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>7. Intellectual Property</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Proto Workbench, including its design, functionality, and content, is owned by EchoLog and is protected 
              by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create 
              derivative works based on the tool without our express written permission.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>8. Modifications to Service</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We reserve the right to modify, suspend, or discontinue Proto Workbench, or any part thereof, at any time 
              with or without notice. We shall not be liable to you or any third party for any modification, suspension, or 
              discontinuance of the service.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>9. Changes to Terms</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We reserve the right to modify these Terms of Service at any time. We will update the "Last Updated" date at the 
              top of this page when changes are made. Your continued use of the tool after such modifications constitutes your 
              acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>10. Contact Information</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:echolog07@gmail.com" className={`${theme.link} underline`}>echolog07@gmail.com</a>.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For comprehensive terms covering all EchoLog services, please visit our{' '}
              <a href="https://echologai.com/terms/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main Terms of Service
              </a>.
            </p>
          </section>
        </div>

        <div className={`mt-8 pt-6 border-t ${theme.border}`}>
          <a 
            href="/" 
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState(null, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className={`inline-flex items-center ${theme.link} hover:underline cursor-pointer`}
          >
            ← Back to Proto Workbench
          </a>
        </div>
      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default TermsPage;

